export enum SegmentationDebugStep {
  ExtractTeeth = 'extractTeeth',
  StripProximal = 'stripProximal',
  GumMesh = 'gumMesh',
  SmoothGumBoundary = 'smoothGumBoundary',
  SnapGum = 'snapGum',
  PapillaAnchors = 'papillaAnchors',
  FillTeeth = 'fillTeeth',
  GumBridges = 'gumBridges',
}

export type SegmentationDebugLogLevel = 'off' | 'warn' | 'debug';

export interface SegmentationDebugConfig {
  stopAfter: SegmentationDebugStep | null;
  logLevel: SegmentationDebugLogLevel;
  /** Extra console + strip-endpoint markers for one tooth (null = all teeth). */
  focusToothIndex: number | null;
  sceneLayers: {
    papilla: boolean;
    snappedGum: boolean;
    /** Unsnapped border strip polylines + first/last endpoints (step 5.5). */
    borderStrips: boolean;
    fillArcs: boolean;
    /** Black spheres on each tooth border vertex after segmentation completes. */
    toothBorders: boolean;
    /**
     * Not a scene layer: downloads a JSON of every tooth's half-edge mesh and
     * wall arcs as they are just before the interproximal fill, for replay in
     * `wallFillFixtures.spec.ts`. It lives here so the dev panel renders a
     * toggle for it automatically.
     */
    dumpWallFixtures: boolean;
  };
}

export const segmentationDebugStepLabels: Record<SegmentationDebugStep, string> = {
  [SegmentationDebugStep.ExtractTeeth]: 'Step 1 - Extract teeth',
  [SegmentationDebugStep.StripProximal]: 'Step 1.5–1.6 - Strip & re-smooth',
  [SegmentationDebugStep.GumMesh]: 'Step 2 - Gum mesh',
  [SegmentationDebugStep.SmoothGumBoundary]: 'Step 4 - Smooth gum boundary',
  [SegmentationDebugStep.SnapGum]: 'Step 5 - Snap gum',
  [SegmentationDebugStep.PapillaAnchors]: 'Step 5.5 - Papilla anchors',
  [SegmentationDebugStep.FillTeeth]: 'Step 6 - Fill teeth',
  [SegmentationDebugStep.GumBridges]: 'Step 7 - Gum bridges',
};

export const defaultSegmentationDebugConfig: SegmentationDebugConfig = {
  stopAfter: null,
  logLevel: 'off',
  focusToothIndex: null,
  sceneLayers: {
    papilla: false,
    snappedGum: false,
    borderStrips: false,
    fillArcs: false,
    toothBorders: false,
    dumpWallFixtures: false,
  },
};
